import gsap from "gsap"
import { useEffect, useRef } from "react"

/**
 * Hook to apply a parallax effect to any element
 *
 * @param options Configuration options for the parallax effect
 * @param options.movementPercent Movement intensity (negative values move opposite to mouse)
 * @param options.duration Animation duration in seconds
 * @param options.canHover Whether hover effects are supported
 * @param options.additionalYOffset Additional Y-axis offset to apply
 * @returns Object containing the ref to attach to the element
 */
export const useParallax = ({
	movementPercent = -2,
	duration = 1.5,
	canHover = true,
	additionalYOffset = 0,
} = {}) => {
	const elementRef = useRef<HTMLImageElement>(null)

	useEffect(() => {
		if (!elementRef.current || !canHover) return

		const settings = {
			duration,
		}

		const elementX = gsap.quickTo(elementRef.current, "xPercent", settings)
		const elementY = gsap.quickTo(elementRef.current, "yPercent", settings)

		const handleMouseMove = (e: MouseEvent) => {
			const mouseX = (e.clientX / window.innerWidth - 0.5) * 2
			const mouseY = (e.clientY / window.innerHeight - 0.5) * 2

			elementX(mouseX * movementPercent)
			elementY(mouseY * movementPercent + additionalYOffset)
		}

		window.addEventListener("mousemove", handleMouseMove)

		return () => {
			window.removeEventListener("mousemove", handleMouseMove)
		}
	}, [movementPercent, duration, canHover, additionalYOffset])

	return { parallaxRef: elementRef }
}
